import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-input-quantidade',
  templateUrl: './input-quantidade.component.html',
  styleUrls: ['./input-quantidade.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputQuantidadeComponent),
      multi: true
    }
  ]
})
export class InputQuantidadeComponent implements ControlValueAccessor {

  @Input() casasDecimais: number = 0;
  @Input() min: number = 0;
  @Input() disabled: boolean = false;
  @Input() style: any = {};

  /** Valor inteiro interno (sem casas decimais) */
  valorInterno: number = 0;

  onChange: (value: number) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private translate: TranslateService) {}

  /** Fator de divisao baseado nas casas decimais */
  private get fator(): number {
    return Math.pow(10, this.casasDecimais);
  }

  /** Valor real (dividido pelo fator) */
  private get valorReal(): number {
    return this.valorInterno / this.fator;
  }

  /** Valor formatado para exibicao no input */
  get displayValue(): string {
    const locale = this.obterLocale();
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: this.casasDecimais,
      maximumFractionDigits: this.casasDecimais,
      useGrouping: true
    }).format(this.valorReal);
  }

  // --- ControlValueAccessor ---

  writeValue(value: any): void {
    if (value == null || isNaN(value)) {
      this.valorInterno = 0;
    } else {
      this.valorInterno = Math.round(value * this.fator);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Eventos ---

  onKeyDown(event: KeyboardEvent): void {
    // Permitir Tab e Enter passarem normalmente (navegacao)
    if (event.key === 'Tab' || event.key === 'Enter') {
      return;
    }

    // Digitos 0-9: empilhar pela direita
    if (event.key >= '0' && event.key <= '9') {
      event.preventDefault();
      const digito = parseInt(event.key, 10);
      this.valorInterno = this.valorInterno * 10 + digito;
      this.onChange(this.valorReal);
      return;
    }

    // Backspace: remover ultimo digito (shift para direita)
    if (event.key === 'Backspace') {
      event.preventDefault();
      this.valorInterno = Math.floor(this.valorInterno / 10);
      this.onChange(this.valorReal);
      return;
    }

    // Qualquer outra tecla: ignorar (prevenir default)
    event.preventDefault();
  }

  onBlur(): void {
    this.onTouched();
  }

  // --- Utilitarios ---

  private obterLocale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'pt-BR';
    if (lang === 'es') return 'es-PY';
    return 'pt-BR';
  }
}
